import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { useSeoSchema } from '@/hooks/useSeoSchema';
import { treatmentServices } from '@/mocks/treatmentServices';
import { cities } from '@/mocks/cities';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://healthtravelturkiye.com';

type InstitutionRow = {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  district: string;
  description: string;
  type: string;
  website?: string;
  logo_url?: string;
  cover_image_url?: string;
  is_approved: boolean;
  is_published: boolean;
  is_pro_member: boolean;
  status: string;
  created_at: string;
  services?: string[];
};

export default function AraciKurumlarPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState(searchParams.get('service') || '');
  const [sortBy, setSortBy] = useState('created_at');
  const [institutions, setInstitutions] = useState<InstitutionRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Resolve service id → serviceKeys
  const activeServiceKeys = useMemo(() => {
    if (!selectedServiceId) return null;
    const found = treatmentServices.find((s) => s.id === selectedServiceId);
    return found ? found.serviceKeys : null;
  }, [selectedServiceId]);

  useEffect(() => {
    const q = searchParams.get('q') || '';
    const service = searchParams.get('service') || '';
    setSearchQuery(q);
    setSelectedServiceId(service);
  }, [searchParams]);

  const loadInstitutions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('institutions')
        .select('*')
        .eq('is_approved', true)
        .eq('is_published', true)
        .eq('status', 'active')
        .eq('type', 'agency')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        setInstitutions([]);
      } else {
        setInstitutions((data || []) as InstitutionRow[]);
      }
    } catch {
      setInstitutions([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadInstitutions(); }, [loadInstitutions]);

  useEffect(() => {
    const channel = supabase
      .channel('araci-kurumlar-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'institutions' }, () => {
        loadInstitutions();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadInstitutions]);

  useEffect(() => {
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    const tc = i18n.getFixedT(i18n.language, 'common');
    const activeSvc = selectedServiceId
      ? treatmentServices.find((s) => s.id === selectedServiceId)
      : null;

    const parts: string[] = [];
    if (activeSvc) parts.push(t(activeSvc.titleKey));
    if (selectedCity) parts.push(selectedCity);

    const baseTitle = tc('seo_agencies_title');
    const pageTitle = parts.length > 0
      ? `${parts.join(' \u00b7 ')} | ${baseTitle}`
      : baseTitle;

    const pageDesc = activeSvc
      ? `Find health tourism agencies offering ${t(activeSvc.titleKey)}${selectedCity ? ` in ${selectedCity}` : ''} organization services on Health Travel T\u00fcrkiye.`
      : selectedCity
        ? `Discover health tourism agencies in ${selectedCity} on Health Travel T\u00fcrkiye.`
        : tc('seo_agencies_desc');

    document.title = pageTitle;
    setMeta('description', pageDesc);
    setMeta('og:title', pageTitle, true);
    setMeta('og:description', pageDesc, true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/d274ee730de1c3e45198b5bb4d4e7d1e.jpg', true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    setMeta('twitter:title', pageTitle);
    setMeta('twitter:description', pageDesc);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=professional%20health%20tourism%20agency%20office%20modern%20travel%20planning%20desk%20organized%20workspace%20maps%20brochures%20Turkey%20medical%20tourism%20services&width=1200&height=630&seq=og-araci-v1&orientation=landscape');
  }, [selectedServiceId, selectedCity, t, i18n.language]);

  useSeoSchema(
    'schema-araci-kurumlar',
    [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Sağlık Turizmi Ajansları',
        description: 'Sağlık hizmetinizi yol, konaklama ve turistik seyahat ihtiyaçlarınızla birlikte organize eden ajanslar.',
        url: `${SITE_URL}/araci-kurumlar`,
      },
    ],
    [{ nameKey: 'breadcrumb_agencies', url: `${SITE_URL}/araci-kurumlar` }],
    i18n.language,
  );

  const filteredInstitutions = institutions
    .filter((inst) => {
      const matchesSearch =
        !searchQuery ||
        inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCity = !selectedCity || inst.city?.toLowerCase() === selectedCity.toLowerCase();
      const matchesService =
        !activeServiceKeys ||
        (inst.services || []).some((s) => activeServiceKeys.includes(s));
      return matchesSearch && matchesCity && matchesService;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name, 'tr');
      if (sortBy === 'city') return a.city.localeCompare(b.city, 'tr');
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
    setSelectedServiceId('');
    setSearchParams({});
  };

  const hasActiveFilter = !!(searchQuery || selectedCity || selectedServiceId);

  return (
    <div className="min-h-screen bg-gray-50 font-['Outfit']">
      <Navbar />

      <div style={{ paddingTop: 'var(--navbar-height, 160px)' }}>
        {/* Hero */}
        <div className="bg-gradient-to-br from-teal-700 to-emerald-600 py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-10">

              {/* Sol: Başlık + Arama */}
              <div className="flex-1 w-full">
                <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
                  <i className="ri-building-4-line"></i>
                  {t('araci_hero_badge')}
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">{t('araci_hero_title')}</h1>
                <p className="text-base text-white/90 mb-8 leading-relaxed max-w-xl">
                  {(() => {
                    const full = t('araci_hero_desc');
                    const strong = t('araci_hero_desc_strong');
                    const idx = full.indexOf(strong);
                    if (idx === -1) return full;
                    return (
                      <>
                        {full.slice(0, idx)}
                        <strong className="text-white">{strong}</strong>
                        {full.slice(idx + strong.length)}
                      </>
                    );
                  })()}
                </p>

                {/* Özellik rozetleri */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {[
                    t('araci_feature_flight'),
                    t('araci_feature_hotel'),
                    t('araci_feature_tour'),
                    t('araci_feature_translation'),
                    t('araci_feature_vip'),
                  ].map((tag) => (
                    <span key={tag} className="flex items-center gap-1.5 bg-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                      <i className="ri-check-line"></i>
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Arama + Şehir */}
                <div className="bg-white rounded-2xl shadow-xl p-4 flex flex-col md:flex-row gap-3">
                  <div className="flex-1 flex items-center px-4 border-b md:border-b-0 md:border-r border-gray-100 pb-3 md:pb-0">
                    <i className="ri-search-line text-xl text-gray-400 mr-3"></i>
                    <input
                      type="text"
                      placeholder={t('araci_search_placeholder')}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        const params: Record<string, string> = {};
                        if (e.target.value) params.q = e.target.value;
                        if (selectedCity) params.city = selectedCity;
                        if (selectedServiceId) params.service = selectedServiceId;
                        setSearchParams(params);
                      }}
                      className="flex-1 py-2 text-sm outline-none text-gray-700"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          const params: Record<string, string> = {};
                          if (selectedCity) params.city = selectedCity;
                          if (selectedServiceId) params.service = selectedServiceId;
                          setSearchParams(params);
                        }}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="px-4 py-2 text-sm outline-none text-gray-700 cursor-pointer min-w-[160px]"
                  >
                    <option value="">{t('araci_all_cities')}</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Sağ: Hizmet İkonları */}
              <div className="hidden lg:flex flex-col gap-3 w-72 flex-shrink-0">
                <p className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-1">{t('araci_services_title')}</p>
                {[
                  { icon: 'ri-flight-takeoff-line', title: t('araci_svc_flight'), desc: t('araci_svc_flight_desc') },
                  { icon: 'ri-hotel-line', title: t('araci_svc_hotel'), desc: t('araci_svc_hotel_desc') },
                  { icon: 'ri-map-pin-2-line', title: t('araci_svc_tour'), desc: t('araci_svc_tour_desc') },
                  { icon: 'ri-translate-2', title: t('araci_svc_translation'), desc: t('araci_svc_translation_desc') },
                ].map((item) => (
                  <div key={item.title} className="flex items-center gap-4 bg-white/15 backdrop-blur rounded-2xl px-4 py-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-white text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white leading-tight">{item.title}</p>
                      <p className="text-xs text-white/60 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>

        {/* İçerik */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {selectedServiceId && (() => {
            const svc = treatmentServices.find((s) => s.id === selectedServiceId);
            return svc ? (
              <div className="mb-5 p-4 rounded-2xl border border-teal-200 bg-teal-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-500 shadow">
                    <i className={`${svc.icon} text-white text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{t(svc.titleKey)}</p>
                    <p className="text-xs text-gray-500">{t(svc.subtitleKey)}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedServiceId('');
                    const params: Record<string, string> = {};
                    if (searchQuery) params.q = searchQuery;
                    if (selectedCity) params.city = selectedCity;
                    setSearchParams(params);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 text-gray-600 rounded-lg text-xs font-medium hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line"></i>
                  {t('inst_remove')}
                </button>
              </div>
            ) : null;
          })()}

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: t('araci_results', { count: filteredInstitutions.length }) }} />
            <div className="flex items-center space-x-3">
              {hasActiveFilter && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line"></i>
                  {t('araci_clear_filters')}
                </button>
              )}
              <span className="text-sm text-gray-600">{t('araci_sort_label')}</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg outline-none text-gray-700 cursor-pointer"
              >
                <option value="created_at">{t('araci_sort_newest')}</option>
                <option value="name">{t('araci_sort_name')}</option>
                <option value="city">{t('araci_sort_city')}</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">{t('araci_loading')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredInstitutions.map((institution) => {
                  const instServices: string[] = institution.services || [];
                  return (
                    <Link
                      key={institution.id}
                      to={`/kurum/${institution.id}`}
                      className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={
                            institution.cover_image_url ||
                            'https://readdy.ai/api/search-image?query=professional%20travel%20agency%20office%2C%20modern%20tourism%20company%20interior%2C%20organized%20travel%20planning%20desk%20with%20maps%20and%20brochures%2C%20bright%20clean%20workspace&width=400&height=300&seq=agency-cover&orientation=landscape'
                          }
                          alt={institution.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        {institution.logo_url && (
                          <div className="absolute top-4 left-4 bg-white p-2 rounded-lg shadow-lg">
                            <img src={institution.logo_url} alt="Logo" className="w-12 h-12 object-contain" />
                          </div>
                        )}
                        {institution.is_pro_member && (
                          <div className="absolute top-4 right-4 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1">
                            <i className="ri-vip-crown-line"></i>
                            PRO
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent px-4 py-3">
                          <span className="inline-flex items-center gap-1 bg-teal-500/90 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">
                            <i className="ri-building-4-line"></i>
                            {t('araci_type_badge')}
                          </span>
                        </div>
                      </div>

                      <div className="p-6">
                        {instServices.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {instServices.slice(0, 3).map((svc) => (
                              <span key={svc} className="px-2.5 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-medium rounded-full">
                                {svc}
                              </span>
                            ))}
                            {instServices.length > 3 && (
                              <span className="px-2.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-medium rounded-full">
                                +{instServices.length - 3}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-xs text-gray-500 flex items-center">
                            <i className="ri-map-pin-line mr-1"></i>
                            {institution.city}{institution.district ? `, ${institution.district}` : ''}
                          </span>
                        </div>

                        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                          {institution.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {institution.description || t('araci_default_desc')}
                        </p>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{institution.city}</span>
                          <span className="text-sm font-medium text-teal-600 flex items-center whitespace-nowrap">
                            {t('araci_view_details')}
                            <i className="ri-arrow-right-line ml-1"></i>
                          </span>
                        </div>

                        {institution.is_pro_member && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                              <i className="ri-vip-crown-line"></i>
                              <span>{t('araci_pro_consult')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>

              {filteredInstitutions.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-20 h-20 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-5">
                    <i className="ri-building-4-line text-4xl text-teal-300"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {hasActiveFilter ? t('araci_empty_title_filter') : t('araci_empty_title_none')}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {hasActiveFilter ? t('araci_empty_desc_filter') : t('araci_empty_desc_none')}
                  </p>
                  {hasActiveFilter && (
                    <button
                      onClick={clearFilters}
                      className="px-6 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('araci_clear_filters')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
